import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AlertsType } from '@rappi/ui/alerts';

import { AlertsService } from '../../../../../services/alerts/alerts.service';
import { MakersFinancesMgrService } from '../../../../services/makers-finances-mgr.service';
import { CompanyDocumentInput, CompanyDocumentRecord } from '../../../../models/makers-finances-mgr.models';
import {
  FH_COMPANY_LABEL,
  FH_FILE_ERROR,
  FH_FILE_FALLBACK,
  FH_FILE_MESSAGE,
  FH_FILE_MIME_MAP,
} from '../../company-form.constants';
import {
  CompanyDocumentTypeConfig,
  CompanyFormSection,
  FhUploadedFile,
} from '../../company-form.types';
import {
  FilesCardLabels,
  FilesCardMessages,
  UploadedFileValue,
} from '../../../shared/files-card/files-card.types';

// The Tax document / Contract cards: section shell + the generic multi-file card.
//
// This wrapper owns everything company-specific: the strict-mode type gate, loading documents
// when a companyId appears, and the persistence hooks — while the company exists, every add is
// POST /companies/:id/documents + re-fetch (the add response carries no ids) and every remove of
// a persisted doc is DELETE /companies/:id/documents, guarded by the CMS `min`. While the
// company doesn't exist yet, the hooks fall through to the card's local-only default and the
// files travel inline in POST /companies' `documents` array instead (see company-form.payload.ts).
@Component({
  selector: 'app-company-files-card',
  templateUrl: './company-files-card.component.html',
  styleUrls: ['./company-files-card.component.scss'],
})
export class CompanyFilesCardComponent implements OnChanges {
  /* istanbul ignore next -- decorator metadata */
  @Input() section: CompanyFormSection;
  /* istanbul ignore next -- decorator metadata */
  @Input() form: FormGroup;
  /* istanbul ignore next -- decorator metadata */
  @Input() submitted = false;
  /* istanbul ignore next -- decorator metadata */
  @Input() required = false;
  /* istanbul ignore next -- decorator metadata */
  @Input() companyId: number | null = null;
  /* istanbul ignore next -- decorator metadata */
  @Input() documentConfig: CompanyDocumentTypeConfig | null = null;
  /* istanbul ignore next -- decorator metadata */
  @Input() strictMode = false;

  readonly TYPE_NOT_ALLOWED_MESSAGE = FH_FILE_MESSAGE[FH_FILE_ERROR.TYPE_NOT_ALLOWED];

  readonly cardLabels: FilesCardLabels = {
    dropzone: FH_COMPANY_LABEL.DROPZONE,
    browse: FH_COMPANY_LABEL.BROWSE,
    uploading: FH_COMPANY_LABEL.UPLOADING,
    preview: FH_COMPANY_LABEL.PREVIEW,
    remove: FH_COMPANY_LABEL.REMOVE,
    download: '', // no download affordance in the company form
  };

  readonly cardMessages: FilesCardMessages = {
    invalidFormat: FH_FILE_MESSAGE[FH_FILE_ERROR.INVALID_FORMAT],
    sizeExceeded: FH_FILE_MESSAGE[FH_FILE_ERROR.SIZE_EXCEEDED],
    maxFilesExceeded: FH_FILE_MESSAGE[FH_FILE_ERROR.MAX_EXCEEDED],
    uploadFailed: FH_FILE_MESSAGE[FH_FILE_ERROR.UPLOAD_FAILED],
    uploadComplete: FH_FILE_MESSAGE['UPLOAD_COMPLETE'],
    removed: FH_FILE_MESSAGE['DOCUMENT_REMOVED'],
    removeFailed: FH_FILE_MESSAGE[FH_FILE_ERROR.REMOVE_FAILED],
  };

  private loadedForCompanyId: number | null = null;

  constructor(
    private readonly financesService: MakersFinancesMgrService,
    private readonly alerts: AlertsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] && this.companyId && this.companyId !== this.loadedForCompanyId) {
      this.loadedForCompanyId = this.companyId;
      this.loadDocuments(this.companyId);
    }
  }

  get field() {
    return this.section?.fields?.[0] ?? null;
  }

  get controlName(): string {
    return this.field?.controlName ?? '';
  }

  get acceptedExtensions(): string[] {
    const configured = this.field?.accept ?? [];
    return configured.length ? configured : FH_FILE_FALLBACK.ACCEPT;
  }

  get cardAcceptMimes(): string[] {
    return this.acceptedExtensions.reduce<string[]>(
      (acc, extension) => acc.concat(FH_FILE_MIME_MAP[extension] ?? []),
      [],
    );
  }

  get maxSizeMb(): number {
    return this.field?.maxSizeMb ?? FH_FILE_FALLBACK.MAX_SIZE_MB;
  }

  get cardMaxSizeBytes(): number {
    return this.maxSizeMb * 1024 * 1024;
  }

  get cardMaxFiles(): number {
    // `max === 0` (or unset) means "no limit" per the CMS contract.
    return this.documentConfig?.max || 0;
  }

  get allowMultiple(): boolean {
    return !!this.field?.multiple;
  }

  // False only in strict mode (CMS has `company_documents.types`) when this field's payload was
  // dropped from the CMS list — see docs/documents/COMPANY_DOCUMENTS_ARCHITECTURE.md §12.
  get isTypeAllowed(): boolean {
    return !this.strictMode || !!this.documentConfig;
  }

  // Hook for the generic card: POST + re-fetch while the company exists, local append otherwise.
  readonly persistAdded = (added: FhUploadedFile[], current: FhUploadedFile[]) => {
    if (!this.companyId) {
      return of(this.allowMultiple ? [...current, ...added] : added);
    }
    const fieldName = this.field?.payload ?? '';
    const payload: CompanyDocumentInput[] = added.map(file => ({
      field_name: fieldName,
      document_key: file.key,
      document_name: file.name,
    }));
    return this.financesService.addCompanyDocuments(this.companyId, payload).pipe(
      switchMap(() => this.financesService.getCompanyDocuments(this.companyId)),
      map(res => this.toUploadedFiles(res.data)),
    );
  };

  // Hook for the generic card: DELETE (min-guarded) for a persisted doc, local splice otherwise.
  readonly persistRemoved = (file: FhUploadedFile, current: FhUploadedFile[]) => {
    if (!this.companyId || !file.id) {
      return of(current.filter(item => item !== file));
    }
    const min = this.documentConfig?.min;
    if (min && current.length - 1 < min) {
      return throwError({ message: FH_FILE_MESSAGE[FH_FILE_ERROR.MIN_VIOLATION] });
    }
    return this.financesService.deleteCompanyDocument(this.companyId, file.id).pipe(
      map(() => current.filter(item => item !== file)),
    );
  };

  // GET /companies/:id/documents on first appearance of a real companyId (edit mode, or a create
  // flow that just got its first id from Save) — prefills this field's files, ids included, so
  // later removals know which document to DELETE.
  private loadDocuments(companyId: number): void {
    this.financesService.getCompanyDocuments(companyId).subscribe({
      next: res => this.setValue(this.toUploadedFiles(res.data)),
      error: () => this.alerts.openAlerts(FH_FILE_MESSAGE[FH_FILE_ERROR.LOAD_FAILED], AlertsType.error),
    });
  }

  private toUploadedFiles(records: CompanyDocumentRecord[]): FhUploadedFile[] {
    const fieldName = this.field?.payload;
    return records
      .filter(doc => doc.field_name === fieldName)
      .map(doc => ({ id: doc.id, key: doc.document_key, name: doc.document_name, uploadedAt: doc.uploaded_at }));
  }

  private setValue(files: UploadedFileValue[]): void {
    const control = this.form?.get(this.controlName);
    control?.setValue(files);
    control?.markAsTouched();
  }
}
